import { Position } from '@/bloks/Page/NavigationPageFindAccountants/components/Filter/components/GeolocationSearch/GeolocationSearch';
import { Accountant, City } from '@/contexts/accountants/types';
import { useSearchParams } from 'next/navigation';
import React, { createContext, useContext, useEffect, useState } from 'react';

export type AccountantData = {
	accountants: Accountant[];
	cities: City[];
	searchAddress: string | undefined;
};

/**
 * List of accountants
 */
export const useAccountants = () => {
	return useContext(Context);
};

interface ContextProps extends AccountantData {
	setSearchAddress: (address: string) => void;
	setCities: (cities: City[]) => void;
	setAccountants: (accountants: Accountant[]) => void;
	currentPage: number;
	setCurrentPage: (page: number) => void;
	totalPages: number;
	setTotalPages: (pages: number) => void;
	geoLocationError?: boolean;
	setGeoLocationError: (error: boolean) => void;
	geoLocation: Position | null;
	setGeoLocation: (position: Position | null) => void;
	filteredAccountants: Accountant[];
	setFilteredAccountants: (accountants: Accountant[]) => void;
	activeFilters: boolean;
	setActiveFilters: (active: boolean) => void;
	searchInput: string | undefined;
	setSearchInput: (input: string) => void;
}

const Context = createContext<ContextProps>({
	accountants: [],
	setAccountants: () => {},
	cities: [],
	setCities: () => {},
	searchAddress: '',
	setSearchAddress: () => {},
	currentPage: 1,
	setCurrentPage: () => {},
	totalPages: 0,
	setTotalPages: () => {},
	geoLocation: {},
	setGeoLocation: () => {},
	setGeoLocationError: () => {},
	filteredAccountants: [],
	setFilteredAccountants: () => {},
	activeFilters: false,
	setActiveFilters: () => {},
	searchInput: '',
	setSearchInput: () => {},
});

interface Props {
	children: React.ReactNode;
	value: AccountantData;
}

export const AccountantsProvider: React.FC<Props> = ({ children, value }) => {
	const [searchAddress, setSearchAddress] = React.useState(value.searchAddress);
	const [accountants, setAccountants] = React.useState(value.accountants);
	const [filteredAccountants, setFilteredAccountants] = React.useState<Accountant[]>([]);
	const [cities, setCities] = React.useState(value.cities);
	const [currentPage, setCurrentPage] = useState(1);
	const [totalPages, setTotalPages] = useState(0);
	const [geoLocation, setGeoLocation] = useState<Position | null>(null);
	const [geoLocationError, setGeoLocationError] = useState(false);
	const [activeFilters, setActiveFilters] = useState(false);
	const [searchInput, setSearchInput] = useState(searchAddress);

	const searchParams = useSearchParams();

	useEffect(() => {
		const address = searchParams.get('address');
		if (address) {
			setSearchAddress(address);
			setSearchInput(address);
		}
	}, [searchParams]);

	return (
		<Context.Provider
			value={{
				accountants,
				setAccountants,
				cities,
				setCities,
				searchAddress,
				setSearchAddress,
				currentPage,
				setCurrentPage,
				totalPages,
				setTotalPages,
				geoLocation,
				setGeoLocation,
				geoLocationError,
				setGeoLocationError,
				filteredAccountants,
				setFilteredAccountants,
				activeFilters,
				setActiveFilters,
				searchInput,
				setSearchInput,
			}}
		>
			{children}
		</Context.Provider>
	);
};
